import { BadgeCheck, FlaskConical, FileText, ClipboardList } from 'lucide-react';
import Reveal from './Reveal';
import SectionHeading from './SectionHeading';
import { IMAGES } from '../../data/site';

const CARDS = [
  {
    icon: BadgeCheck,
    title: 'Quality Focused',
    text: 'Product quality is evaluated according to buyer requirements and applicable specifications.',
  },
  {
    icon: FlaskConical,
    title: 'COA / Testing',
    text: 'Batch-level COA and applicable testing documentation can be provided where available.',
  },
  {
    icon: FileText,
    title: 'Export Documentation',
    text: 'Documentation is prepared according to transaction and destination requirements.',
  },
  {
    icon: ClipboardList,
    title: 'Buyer Requirements',
    text: 'Specifications, packaging and commercial requirements are discussed before order confirmation.',
  },
];

export const Quality = () => (
  <section id="quality" data-testid="quality-section" className="relative py-24 sm:py-32">
    <div className="mx-auto max-w-7xl px-5 sm:px-8">
      <div className="grid grid-cols-1 items-end gap-10 lg:grid-cols-12">
        <div className="lg:col-span-7">
          <SectionHeading
            eyebrow="Quality & Documentation"
            title={
              <>
                Quality that travels with <span className="accent-italic font-semibold">every shipment.</span>
              </>
            }
          />
        </div>
        <Reveal delay={0.15} className="lg:col-span-5">
          <p className="text-sm leading-relaxed text-muted-foreground">
            Applicable registrations and documentation are maintained as required for the relevant product and
            shipment. We discuss requirements openly before any order is confirmed.
          </p>
        </Reveal>
      </div>

      <div className="mt-14 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {CARDS.map((card, i) => (
          <Reveal key={card.title} delay={i * 0.1}>
            <div
              data-testid={`quality-card-${i}`}
              className="group h-full rounded-3xl border border-line bg-white p-7 shadow-card transition-all duration-500 hover:-translate-y-1.5 hover:border-gold/50 hover:shadow-lift"
            >
              <div className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-sage-mist text-forest transition-colors duration-500 group-hover:bg-forest group-hover:text-cream">
                <card.icon className="h-6 w-6" />
              </div>
              <h3 className="mt-5 font-display text-lg font-semibold text-ink">{card.title}</h3>
              <p className="mt-2.5 text-sm leading-relaxed text-muted-foreground">{card.text}</p>
            </div>
          </Reveal>
        ))}
      </div>

      <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-2">
        <Reveal>
          <div className="group relative overflow-hidden rounded-[2rem] shadow-card">
            <img
              src={IMAGES.qualityLab}
              alt="Quality testing in a laboratory environment"
              loading="lazy"
              className="aspect-[16/9] w-full object-cover transition-transform duration-[1.6s] ease-out group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-forest/70 via-forest/10 to-transparent" />
            <p className="absolute bottom-5 left-6 text-sm font-bold uppercase tracking-[0.2em] text-cream">
              Batch-Level Attention
            </p>
          </div>
        </Reveal>
        <Reveal delay={0.12}>
          <div className="group relative overflow-hidden rounded-[2rem] shadow-card">
            <img
              src={IMAGES.qualityDocs}
              alt="Export documentation being prepared"
              loading="lazy"
              className="aspect-[16/9] w-full object-cover transition-transform duration-[1.6s] ease-out group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-forest/70 via-forest/10 to-transparent" />
            <p className="absolute bottom-5 left-6 text-sm font-bold uppercase tracking-[0.2em] text-cream">
              Documented Shipments
            </p>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

export default Quality;
